"use client";

import React, { useEffect, useState } from "react";
import { ArrowRight, ExternalLink, Wrench, Settings, Ruler, TriangleAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function TransformYourVision() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const [mousePosition, setMousePosition] = useState({
    x: 0,
    y: 0
  });
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({
        x: e.clientX / window.innerWidth * 20 - 10,
        y: e.clientY / window.innerHeight * 20 - 10
      });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);
  return <section data-section-id="36558" id="the-future-of-cnc-uptime" ref={sectionRef} className="dark bg-background text-foreground relative min-h-[90vh] flex items-center overflow-hidden py-24 px-4 sm:px-8">
      {}
      <div className="absolute inset-0 z-0 opacity-5 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,rgba(34,197,94,0.1),transparent_50%)]" />
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="grid-pattern" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeOpacity="0.1" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid-pattern)" />
        </svg>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto w-full grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        {}
        <div className={`lg:col-span-7 flex flex-col gap-6 transition-all duration-1000 transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <h1 className="font-sans text-5xl md:text-6xl lg:text-[52.8px] font-bold leading-[1.1] tracking-tighter text-white">
            The World's Smartest Service Technician.{" "}
            <span className="bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent drop-shadow-[0_0_15px_rgba(34,197,94,0.4)]">
              At Your Fingertips.
            </span>
          </h1>

          <p className="font-sans text-lg md:text-[20.8px] text-muted-foreground leading-[1.6] max-w-2xl">Modern Ai meets world-class CNC service knowledge. Empower your shop with the CNCWise software platform: an Ai powered virtual service technician, an advanced preventative maintenance dashboard, as well as exclusive component repair discounts. Subscribing to CNCWise is like having a master service technician on your shop floor 24/7.</p>

          <div className="flex flex-wrap items-center gap-4 mt-4">
            <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm px-8 py-6 text-base font-bold transition-all hover:shadow-[0_0_30px_rgba(34,197,94,0.5)]">
              <Link to="#cncwise-ecosystem">
                Explore the Portal
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>

            <Button asChild variant="outline" className="bg-transparent border-primary text-primary hover:bg-primary/10 rounded-sm px-8 py-6 text-base font-bold transition-all">
              <Link to="https://app.cncwise.com/signup">
                Get Started
                <ExternalLink className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>

        {}
        <div className={`lg:col-span-5 relative flex justify-center items-center transition-all duration-1000 delay-300 transform ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-10"}`}>
          {}
          <div className="absolute inset-0 bg-primary/10 blur-[100px] rounded-lg transform scale-75" />

          {}
          <div className="relative w-full max-w-[500px] aspect-square" style={{
          transform: `perspective(1000px) rotateY(${mousePosition.x}deg) rotateX(${-mousePosition.y}deg)`,
          transition: "transform 0.1s ease-out"
        }}>
            <svg viewBox="0 0 500 500" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full drop-shadow-2xl">
              {}
              <rect x="50" y="100" width="400" height="300" rx="4" fill="hsl(220 15% 10%)" stroke="hsl(142 76% 45%)" strokeWidth="2" className="animate-[pulse_4s_ease-in-out_infinite]" />
              
              {}
              <path d="M100 400 L100 350 L150 300 L350 300 L400 250" stroke="hsl(142 76% 45%)" strokeWidth="3" strokeLinecap="round" className="opacity-40" />
              <path d="M400 100 L400 150 L350 200 L150 200 L100 250" stroke="hsl(142 76% 45%)" strokeWidth="3" strokeLinecap="round" className="opacity-40" />
              
              {}
              <circle r="4" fill="#fff">
                <animateMotion dur="3s" repeatCount="indefinite" path="M100 400 L100 350 L150 300 L350 300 L400 250" />
              </circle>
              <circle r="4" fill="#fff">
                <animateMotion dur="4s" repeatCount="indefinite" path="M400 100 L400 150 L350 200 L150 200 L100 250" />
              </circle>

              {}
              <g transform="translate(220, 220)">
                <rect x="-30" y="-30" width="60" height="60" rx="30" fill="hsl(142 76% 45%)" fillOpacity="0.3" stroke="hsl(142 76% 45%)" className="animate-pulse" />
                <Wrench className="text-primary w-10 h-10 -translate-x-5 -translate-y-5" />
              </g>

              <g className="animate-[bounce_3s_infinite]">
                <rect x="120" y="150" width="50" height="50" rx="4" fill="hsl(220 15% 15%)" stroke="hsl(142 76% 45%)" />
                <Settings className="text-primary w-6 h-6 x-132 y-162 translate-x-[133px] translate-y-[163px]" />
              </g>

              <g className="animate-[bounce_4s_infinite_1s]">
                <rect x="330" y="150" width="50" height="50" rx="4" fill="hsl(220 15% 15%)" stroke="hsl(48 96% 53%)" />
                <Ruler className="text-secondary w-6 h-6 translate-x-[343px] translate-y-[163px]" />
              </g>

              <g className="animate-[bounce_3.5s_infinite_0.5s]">
                <rect x="225" y="320" width="50" height="50" rx="4" fill="hsl(220 15% 15%)" stroke="hsl(0 84% 60%)" />
                <TriangleAlert className="text-destructive w-6 h-6 translate-x-[238px] translate-y-[333px]" />
              </g>

              {}
              <circle cx="60" cy="110" r="3" fill="hsl(142 76% 45%)" />
              <circle cx="440" cy="110" r="3" fill="hsl(142 76% 45%)" />
              <circle cx="60" cy="390" r="3" fill="hsl(142 76% 45%)" />
              <circle cx="440" cy="390" r="3" fill="hsl(142 76% 45%)" />
            </svg>
          </div>
        </div>
      </div>
    </section>;
}